import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import CheckBox from 'components/form/CheckBox'
import { Link } from 'react-router-dom'
import { failWithErrorResponse, success } from 'features/toast/Toast'
import {
  fetchCampaigns,
  fetchFilteredAdGroups,
  resetAdGroupCopy,
  setCopyAsOff,
  setSelectedCampaignId,
} from 'features/dashboard/operation/adGroupCopy/copyAdGroupsSlice'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import api from 'api/axios/foka'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import SelectBox from 'components/form/SelectBox'
import { isPresent } from 'utils'
import Loading from 'components/loading/Loading'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'

const CopyAdGroupsDialog = ({ handleClose }) => {
  const dispatch = useDispatch()
  const [isSubmitting, setSubmitting] = useState(false)
  const {
    adAccountId,
    copyAdGroups: {
      campaigns,
      selectedCampaignId,
      filteredAdGroups,
      copyAsOff,
      campaignLoading,
      adGroupLoading,
    },
    adGroupTable: { selectedIds, content },
  } = useSelector(
    ({ account, dashboard }) => ({
      adAccountId: account.current.id,
      copyAdGroups: dashboard.operation.copyAdGroups,
      adGroupTable: dashboard.table.adGroupTable,
    }),
    shallowEqual
  )

  useEffect(() => {
    if (selectedIds.length > 0) {
      const bizChannelId = content.find(({ id }) => id === selectedIds[0])
        ?.parentDomainInfo?.bizChannel?.id
      dispatch(fetchCampaigns(bizChannelId))
    }
  }, [dispatch, selectedIds, content])

  useEffect(() => {
    if (selectedIds.length > 0) {
      dispatch(fetchFilteredAdGroups())
    }
  }, [dispatch, selectedIds])

  useEffect(() => () => dispatch(resetAdGroupCopy()), [dispatch])

  const handleSelectCampaign = v => {
    dispatch(setSelectedCampaignId(v))
  }

  const handleConfirm = async ({ preventClose }) => {
    setSubmitting(true)
    try {
      await api.ad.copyAdGroups({
        campaignId: selectedCampaignId,
        adGroupIds: filteredAdGroups.map(adGroup => adGroup.id),
        copyAsOff,
      })
      success('선택한 광고그룹을 복사하였습니다.')
      setSubmitting(false)
      dispatch(refreshDashboard())
    } catch (e) {
      preventClose()
      setSubmitting(false)
      failWithErrorResponse(e?.response?.data, {
        messageMap: {
          104007: '이미 삭제된 그룹은 복사할 수 없습니다.',
          104013: '복사할 광고그룹을 1개이상 선택해야 합니다.',
          104014: '복사할 광고그룹을 10개이하로 선택해야 합니다.',
          104015: '비즈채널이 같은 캠페인으로만 광고그룹 복사가 가능합니다.',
          104016: '이미 복사가 진행되고 있는 광고그룹이 포함되어 있습니다.',
        },
      })
    }
  }

  return (
    <ConfirmModal
      title="광고그룹 복사"
      onConfirm={handleConfirm}
      handleClose={handleClose}
      confirmDisabled={
        isSubmitting ||
        campaignLoading ||
        adGroupLoading ||
        filteredAdGroups.length === 0 ||
        campaigns.length === 0 ||
        selectedCampaignId === null
      }>
      <p className="desc_copy">
        <span className="fc_warn">비즈채널이 동일한 캠페인</span>으로만 복사
        가능합니다.
        <br />
        복사 완료까지 걸리는 시간은 데이터 크기에 따라 유동적일 수 있으며, 진행
        결과는
        <br />
        <Link to={`/${adAccountId}/bulks/group-copy`} className="fc_empty">
          광고자산 관리 &gt; 대량관리
        </Link>
        에서 확인 할 수 있습니다.
      </p>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 광고그룹이 복사될 캠페인
          <HelpTooltip>
            비즈채널이 동일한 캠페인으로만 복사 가능합니다
          </HelpTooltip>
        </strong>
        {campaigns.length === 0 && !campaignLoading ? (
          <p className="txt_info">
            <span className="ico_comm" />
            선택 가능한 캠페인이 존재하지 않습니다.
          </p>
        ) : (
          <LoadingWrapper isLoading={campaignLoading}>
            <SelectBox
              value={selectedCampaignId}
              noBlurOnDefault={selectedCampaignId !== null}
              onValueChange={handleSelectCampaign}
              placeholder="캠페인 선택">
              {(campaigns || []).map(campaign => (
                <SelectBox.Item key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectBox.Item>
              ))}
            </SelectBox>
          </LoadingWrapper>
        )}
      </div>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 광고그룹{' '}
          <span className="fc_emph">{filteredAdGroups.length}</span>
          <HelpTooltip nowrap={false}>
            광고그룹은 최대 10까지 선택 후 복사하실 수 있으며, 광고그룹에 입력할
            수 있는 기준인 키워드 1,000개와 연결소재 20개를 초과한 광고그룹은
            복사 대상에 포함되지 않습니다.
          </HelpTooltip>
        </strong>
        <ul className="list_select">
          <li>광고그룹명</li>
          {adGroupLoading ? (
            <li>
              <Loading />
            </li>
          ) : isPresent(filteredAdGroups) ? (
            filteredAdGroups.map(({ id, name }) => <li key={id}>{name}</li>)
          ) : (
            <li>
              <span className="fc_info">복사 가능한 광고그룹 없습니다.</span>
            </li>
          )}
        </ul>
      </div>
      <div className="select_ad">
        <strong className="tit_copy">추가 설정</strong>
        <CheckBox
          label="광고그룹 OFF 상태로 복사"
          checked={copyAsOff}
          onCheckedChange={checked => {
            dispatch(setCopyAsOff(checked))
          }}
        />
      </div>
    </ConfirmModal>
  )
}

CopyAdGroupsDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
}

export default CopyAdGroupsDialog
